import angular, { IScope } from 'angular';
import { IFieldControllerField } from './field-controller';
import { IFormModel } from '../form-controller';
import { SharedAngular } from '../../@types/sharedAngular';
import {
  IExternalBusinessDetail,
  ISubFieldVisibility
} from '@Shared.Angular/@types/core/contracts/queryModel/externalBusinessProvider/externalBusinessProvider';
import {
  EXTERNAL_BUSINESS_SUBFIELDS,
  EXTERNAL_BUSINESS_FIELD_PLACEHOLDERS
} from '@Shared.Angular/@types/core/contracts/constants/externalBusinessFieldLabels';

const DEFAULT_SUBFIELD_VISIBILITY: ISubFieldVisibility = {
  entityName: true,
  id: true,
  industryClassificationCode: true,
  industryDescription: true,
  entityStatus: false,
  businessType: false,
  entityType: false,
  registrationDate: false,
  directorInformation: false,
  registeredAddress: false
};

interface IExternalBusinessField extends IFieldControllerField {
  $dirty?: boolean;
}

interface IBusinessSubfield {
  key: string;
  label: string;
}

interface IExternalBusinessFieldScope extends IScope {
  field: IExternalBusinessField;
  form: IFormModel;
  formCtrl?: {
    businessId?: string;
    isPublic?: boolean;
  };
  options: any;
  businessDisplayValue: string;
  isLoading: boolean;
  editMode: boolean;
  businessSubfields: IBusinessSubfield[];
  businessFieldPlaceholders: Record<string, string>;
  parsedBusiness: IExternalBusinessDetail | null;
  isManualEntry: boolean;
  toggleManualEntry: () => void;
  isSubFieldEditable: () => boolean;
  getSubFieldDisplayValue: (key: string) => string;
  updateBusinessData: () => void;
}

angular
  .module('fg')
  .directive('fgFieldExternalBusiness', [
    'fgFieldExternalBusinessLinkFn',
    function (fgFieldExternalBusinessLinkFn) {
      return {
        replace: true,
        templateUrl:
          'angular-form-gen/field-templates/default/externalbusiness.ng.html',
        scope: true,
        link: fgFieldExternalBusinessLinkFn,
        require: ['^?fgForm']
      };
    }
  ])
  .factory('fgFieldExternalBusinessLinkFn', [
    'externalBusinessLookupService',
    function (
      externalBusinessLookupService: SharedAngular.ExternalBusinessLookupService
    ) {
      return function (
        $scope: IExternalBusinessFieldScope,
        $element,
        $attrs,
        ctrls
      ) {
        const fgFormCtrl = ctrls[0];
        const field = $scope.field;
        const form = $scope.form;
        const schema = field.schema;
        let isSelectedFromList = false;
        let currentSearchText = '';
        let currentInputValue = '';
        let isUpdatingFromCode = false;

        if (!schema.subFieldVisibility) {
          schema.subFieldVisibility = { ...DEFAULT_SUBFIELD_VISIBILITY };
        }
        // Ensure entityName is always visible
        schema.subFieldVisibility.entityName = true;

        if (!form.data[schema.name]) {
          form.data[schema.name] = null;
        }

        $scope.businessDisplayValue = '';
        $scope.isLoading = false;
        $scope.parsedBusiness = null;
        $scope.isManualEntry = false;
        const MIN_QUERY_LENGTH = 3;
        const SEARCH_LIMIT = 10;

        // Define subfield configuration for template rendering
        $scope.businessSubfields = EXTERNAL_BUSINESS_SUBFIELDS;
        $scope.businessFieldPlaceholders = EXTERNAL_BUSINESS_FIELD_PLACEHOLDERS;

        const isPublicForm = fgFormCtrl?.isPublic === true;
        const formBusinessId = isPublicForm
          ? fgFormCtrl?.businessId || ''
          : undefined;
        $scope.editMode = fgFormCtrl?.editMode || false;

        // Parse stored business data for display
        function updateParsedBusiness() {
          const storedValue = form.data[schema.name];
          if (storedValue) {
            try {
              $scope.parsedBusiness = JSON.parse(storedValue);
              const displayValue = $scope.parsedBusiness?.entityName || '';
              $scope.businessDisplayValue = displayValue;
              currentInputValue = displayValue;
              currentSearchText = displayValue;
              isSelectedFromList = true;

              // Restore isManualEntry from stored data
              if ($scope.parsedBusiness?.isManualEntry !== undefined) {
                $scope.isManualEntry = $scope.parsedBusiness.isManualEntry;
              }

              // Format null values as "Not Available" for readonly display
              if ($scope.parsedBusiness && !$scope.isManualEntry) {
                Object.keys($scope.parsedBusiness).forEach((key) => {
                  if ($scope.parsedBusiness[key] === null) {
                    $scope.parsedBusiness[key] = 'Not Available';
                  }
                });
              }
            } catch (e) {
              $scope.parsedBusiness = null;
            }
          } else {
            $scope.parsedBusiness = null;
          }
        }

        // Initialize display value from stored data
        updateParsedBusiness();

        const businessDataSource = new kendo.data.DataSource({
          serverFiltering: true,
          transport: {
            read: function (options) {
              const searchText = options.data.filter?.filters?.[0]?.value || '';

              if (searchText.length < MIN_QUERY_LENGTH) {
                options.success([]);
                return;
              }

              if (!schema.externalBusinessProviderId) {
                console.error(
                  'No business provider configured for field:',
                  schema.name
                );
                options.success([]);
                return;
              }

              $scope.isLoading = true;

              const searchPromise =
                externalBusinessLookupService.searchBusinesses(
                  searchText,
                  schema.externalBusinessProviderId,
                  SEARCH_LIMIT,
                  formBusinessId
                );

              searchPromise
                .then(function (results) {
                  const kendoData = results.map(function (item) {
                    return {
                      id: item.id,
                      name: item.name
                    };
                  });
                  options.success(kendoData);
                })
                .catch(function (error) {
                  console.error('Business search error:', error);
                  options.success([]);
                })
                .finally(function () {
                  $scope.isLoading = false;
                });
            }
          }
        });

        $scope.options = {
          dataSource: businessDataSource,
          dataTextField: 'name',
          filter: 'contains',
          minLength: MIN_QUERY_LENGTH,
          placeholder: schema.placeholder,
          select: function (e) {
            const dataItem = e.dataItem;
            if (dataItem?.id) {
              isSelectedFromList = true;
              $scope.isLoading = true;

              const detailsPromise =
                externalBusinessLookupService.getBusinessDetails(
                  dataItem.id,
                  schema.externalBusinessProviderId,
                  formBusinessId
                );

              detailsPromise
                .then(function (details) {
                  const businessName =
                    externalBusinessLookupService.formatBusinessName(details);
                  e.sender.value(businessName);
                  currentSearchText = businessName;
                  currentInputValue = businessName;
                  $scope.businessDisplayValue = businessName;

                  // Add isManualEntry flag to stored data
                  const businessData = {
                    ...details,
                    entityName: businessName,
                    isManualEntry: false
                  };

                  // Format null values as "Not Available" for readonly display
                  Object.keys(businessData).forEach((key) => {
                    if (businessData[key] === null) {
                      businessData[key] = 'Not Available';
                    }
                  });

                  form.data[schema.name] = JSON.stringify(businessData);
                  $scope.parsedBusiness = businessData;
                  $scope.isManualEntry = false;

                  if (field.state) {
                    field.state.$setValidity('businessSelection', true);
                  }
                  field.$dirty = true;
                })
                .catch(function (error) {
                  console.error('Failed to get business details:', error);
                  if (field.state) {
                    field.state.$setValidity('businessSelection', false);
                  }
                })
                .finally(function () {
                  $scope.isLoading = false;
                });
            }
          },
          change: function (e) {
            const value = e.sender.value();
            currentInputValue = value || '';

            if (isSelectedFromList && value !== currentSearchText) {
              isSelectedFromList = false;
              form.data[schema.name] = null;
              $scope.parsedBusiness = null;
              field.$dirty = true;
            }

            if (!value?.trim()) {
              isSelectedFromList = false;
              form.data[schema.name] = null;
              currentSearchText = '';
              $scope.businessDisplayValue = '';
              $scope.parsedBusiness = null;

              if (field.state) {
                field.state.$setValidity('businessSelection', true);
              }
            }

            validateBusiness();
          },
          close: function () {
            validateBusiness();
          }
        };

        function validateBusiness() {
          // Only validate if field is required
          if (!field.schema.validation?.required) {
            if (field.state) {
              field.state.$setValidity('businessSelection', true);
            }
            return true;
          }

          // In manual entry mode, don't validate the autocomplete field
          if ($scope.isManualEntry) {
            if (field.state) {
              field.state.$setValidity('businessSelection', true);
            }
            return true;
          }

          // In lookup mode, check if user typed something but didn't select from list
          if (currentInputValue?.trim() && !isSelectedFromList) {
            if (field.state) {
              field.state.$setValidity('businessSelection', false);
            }
            return false;
          }

          if (field.state) {
            field.state.$setValidity('businessSelection', true);
          }
          return true;
        }

        $scope.$on('fg:validate', function () {
          validateBusiness();
        });

        $scope.toggleManualEntry = function () {
          isUpdatingFromCode = true;
          isSelectedFromList = false;
          $scope.businessDisplayValue = '';
          currentInputValue = '';
          currentSearchText = '';

          const emptyBusinessLookupDetail: IExternalBusinessDetail = {
            id: '',
            entityName: '',
            industryClassificationCode: '',
            industryDescription: '',
            entityStatus: '',
            businessType: '',
            entityType: '',
            registrationDate: '',
            directorInformation: '',
            registeredAddress: '',
            isManualEntry: false
          };
          if ($scope.isManualEntry) {
            emptyBusinessLookupDetail.isManualEntry = true;

            form.data[schema.name] = JSON.stringify(emptyBusinessLookupDetail);
            $scope.parsedBusiness = emptyBusinessLookupDetail;
          } else {
            form.data[schema.name] = null;
            $scope.parsedBusiness = emptyBusinessLookupDetail;
          }

          isUpdatingFromCode = false;

          if (field.state) {
            field.state.$setValidity('businessSelection', true);
          }

          field.$dirty = true;
        };

        $scope.isSubFieldEditable = function () {
          return $scope.isManualEntry;
        };

        // Helper function to get display value for subfields in readonly mode
        $scope.getSubFieldDisplayValue = function (key: string): string {
          if (!$scope.parsedBusiness) {
            return '';
          }
          const value = $scope.parsedBusiness[key];
          if (value === null) {
            return 'Not Available';
          }
          return value || '';
        };

        $scope.updateBusinessData = function () {
          if (
            !isUpdatingFromCode &&
            $scope.isManualEntry &&
            $scope.parsedBusiness
          ) {
            form.data[schema.name] = JSON.stringify($scope.parsedBusiness);
            field.$dirty = true;
          }
        };
      };
    }
  ]);
